import React, { memo, lazy, Suspense, useState } from "react";
import { useApp } from "../context/AppContext";
import ErrorBoundary from "../components/ui/ErrorBoundary";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import SEOHead from "../components/seo/SEOHead";
import {
  getOrganizationSchema,
  getWebSiteSchema,
  getSoftwareApplicationSchema,
} from "../data/structuredData";
import FuturisticKumiaLanding from "../components/FuturisticKumiaLanding";
import useExitIntent from "../hooks/useExitIntent";

// Lazy load heavy components for better performance
const FeaturesShowcase = lazy(() => import("../components/FeaturesShowcase"));
const KumIACycleVisualization = lazy(() => import("../components/KumIACycleVisualization"));
const FinalPricing = lazy(() => import("../components/FinalPricing"));
const FuturisticFooter = lazy(() => import("../components/FuturisticFooter"));
const ROICalculator = lazy(() => import("../components/ROICalculator"));
const SignInSignUp = lazy(() => import("../components/SignInSignUp"));
const WhatsAppButton = lazy(() => import("../components/WhatsAppButton"));

const PrivacyPolicy = lazy(() => import("../components/PrivacyPolicy"));
const TermsOfService = lazy(() => import("../components/TermsOfService"));
const CookiePolicy = lazy(() => import("../components/CookiePolicy"));
const SecurityBanner = lazy(() => import("../components/SecurityBanner"));
const PPMSection = lazy(() => import("../components/PPMSection"));
const CTASection = lazy(() => import("../components/CTASection"));
const FAQSection = lazy(() => import("../components/FAQSection"));
const ExitIntentModal = lazy(() => import("../components/ExitIntentModal"));

const HomePage = memo(() => {
  const {
    language,
    setLanguage,
    showROICalculator,
    toggleROICalculator,
    showAuthModal,
    authMode,
    showSignIn,
    showSignUp,
    hideAuthModal,
  } = useApp();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);

  // Exit-intent: captura visitantes que están por irse y los lleva al funnel
  const { shouldShow: showExitIntent, dismiss: dismissExitIntent } = useExitIntent();

  return (
    <ErrorBoundary>
      <SEOHead
        title="Software Gastronomico con Gamificacion, CRM y Pedidos Digitales para Restaurantes"
        description="KumIA es el ecosistema digital para restaurantes. Software gastronomico con sistema de pedidos QR, CRM, fidelizacion con gamificacion y dashboard en tiempo real. Digitaliza tu restaurante desde $500/mes."
        canonical="/"
        keywords="software gastronomico, sistema para restaurantes, CRM para restaurantes, menu digital para restaurantes, gamificacion restaurantes, fidelizacion de clientes restaurantes, pedidos digitales, ecosistema digital, gestion de restaurantes, dashboard restaurante, tecnologia para restaurantes, software para restaurantes"
        jsonLd={[
          getOrganizationSchema(),
          getWebSiteSchema(),
          getSoftwareApplicationSchema(),
        ]}
      />
      <main id="main-content" className="overflow-x-hidden">
        <FuturisticKumiaLanding
          onShowSignIn={showSignIn}
          onShowSignUp={showSignUp}
        />

        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <FeaturesShowcase />
        </Suspense>

        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <FinalPricing onShowSignUp={showSignUp} />
        </Suspense>

        {/* PPMSection oculta — se reactiva post-50 founders
        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <PPMSection />
        </Suspense>
        */}

        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <KumIACycleVisualization />
        </Suspense>

        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <FAQSection />
        </Suspense>

        <Suspense fallback={<div className="h-48 flex items-center justify-center"><LoadingSpinner /></div>}>
          <SecurityBanner />
        </Suspense>

        <Suspense fallback={<div className="h-48 flex items-center justify-center"><LoadingSpinner /></div>}>
          <CTASection onShowSignUp={showSignUp} />
        </Suspense>
        
        <Suspense fallback={<div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>}>
          <FuturisticFooter 
            onShowPrivacyPolicy={() => setShowPrivacyPolicy(true)}
            onShowTermsOfService={() => setShowTermsOfService(true)}
            onShowCookiePolicy={() => setShowCookiePolicy(true)}
          />
        </Suspense>
        
        <Suspense fallback={<div className="w-16 h-16"><LoadingSpinner /></div>}>
          <WhatsAppButton language={language} />
        </Suspense>
        
        {/* Modals */}
        <Suspense fallback={null}>
          <ROICalculator 
            language={language}
            isVisible={showROICalculator}
            onClose={() => toggleROICalculator(false)}
            onShowSignUp={() => {
              toggleROICalculator(false);
              showSignUp();
            }}
          />
        </Suspense>
        
        <Suspense fallback={null}>
          <SignInSignUp
            isVisible={showAuthModal}
            onClose={hideAuthModal}
            initialMode={authMode}
            language={language}
          />
        </Suspense>

        <Suspense fallback={null}>
          <PrivacyPolicy
            isVisible={showPrivacyPolicy}
            onClose={() => setShowPrivacyPolicy(false)}
            language={language}
          />
        </Suspense>

        <Suspense fallback={null}>
          <TermsOfService
            isVisible={showTermsOfService}
            onClose={() => setShowTermsOfService(false)}
            language={language}
          />
        </Suspense>

        <Suspense fallback={null}>
          <CookiePolicy
            isVisible={showCookiePolicy}
            onClose={() => setShowCookiePolicy(false)}
            language={language}
          />
        </Suspense>

        <Suspense fallback={null}>
          <ExitIntentModal isOpen={showExitIntent} onClose={dismissExitIntent} />
        </Suspense>
      </main>
    </ErrorBoundary>
  );
});

HomePage.displayName = 'HomePage';

export default HomePage;